import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Loader2, Trash2, AlertTriangle, CheckCircle, FlaskConical } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestAccountsPanel() {
  const [data, setData] = useState({ tenants: [], users: [], clients: [], employees: [] });
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);

  const fetchTestAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/owner-console/test-accounts`);
      setData(res.data || { tenants: [], users: [], clients: [], employees: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load test accounts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTestAccounts(); }, [fetchTestAccounts]);

  const handlePurgeAll = async () => {
    setPurging(true);
    try {
      const res = await axios.post(`${API}/owner-console/test-accounts/purge-all`);
      const d = res.data?.deleted || {};
      const total = (d.tenants || 0) + (d.users || 0) + (d.clients || 0) + (d.employees || 0);
      toast.success(`Purged ${total} test record(s): ${d.tenants} tenants, ${d.users} users, ${d.clients} clients, ${d.employees} employees`);
      fetchTestAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Purge failed");
    } finally {
      setPurging(false);
      setConfirmPurge(false);
    }
  };

  const handleDeleteTenant = async (tenant) => {
    if (!window.confirm(`Delete tenant "${tenant.name}" and its data permanently?`)) return;
    try {
      await axios.delete(`${API}/owner-console/test-accounts/tenant/${tenant.id}`);
      toast.success("Tenant deleted");
      fetchTestAccounts();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const total = data.tenants.length + data.users.length + data.clients.length + data.employees.length;
  const allClean = total === 0;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            Test Accounts
          </h3>
          <p className="text-sm text-muted-foreground">
            Records matching test patterns (TEST_*, *@test.com, launch_*) or flagged as test. These are <strong>excluded</strong> from your live subscriber metrics.
          </p>
        </div>
        {!allClean && (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setConfirmPurge(true)}
            disabled={purging}
            data-testid="purge-all-test-btn"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Purge all ({total})
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : allClean ? (
        <Card className="border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800">
          <CardContent className="py-8 text-center">
            <CheckCircle className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
            <p className="font-medium text-emerald-900 dark:text-emerald-200">No test accounts</p>
            <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-1">
              Your platform is production-clean.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {data.tenants.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  Test tenants <Badge variant="secondary">{data.tenants.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.tenants.map(t => (
                  <div key={t.id} className="flex items-center justify-between gap-2 p-2 rounded border bg-card" data-testid={`test-tenant-${t.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">{t.name}</p>
                        <Badge variant="outline" className="text-xs">{t.subscription_plan}</Badge>
                        <Badge variant="outline" className="text-xs">{t.subscription_status}</Badge>
                        {t.is_test_account && <Badge variant="secondary" className="text-xs">flagged</Badge>}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteTenant(t)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {data.users.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  Test users <Badge variant="secondary">{data.users.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {data.users.map(u => (
                    <div key={u.id} className="p-2 rounded border bg-card text-sm">
                      <p className="font-medium truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                      <p className="text-xs text-muted-foreground">{u.role}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {(data.clients.length > 0 || data.employees.length > 0) && (
            <div className="grid gap-3 md:grid-cols-2">
              {data.clients.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      Test clients <Badge variant="secondary">{data.clients.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1 text-sm">
                    {data.clients.map(c => (
                      <div key={c.id} className="truncate text-muted-foreground">
                        {c.name} · {c.email || "no email"}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              {data.employees.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      Test employees <Badge variant="secondary">{data.employees.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1 text-sm">
                    {data.employees.map(e => (
                      <div key={e.id} className="truncate text-muted-foreground">
                        {e.name} · {e.email || "no email"}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      <AlertDialog open={confirmPurge} onOpenChange={setConfirmPurge}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Purge all test accounts?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <strong>{total}</strong> test record(s) across tenants, users, clients, and employees.
              Your master account (businessgenie.bg@gmail.com) is never affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={purging}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePurgeAll} className="bg-red-600 hover:bg-red-700" disabled={purging} data-testid="confirm-purge-all-btn">
              {purging && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Purge all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
